import axios from 'axios';

// API base URL — see services/api.ts for the CRA build-time env boundary
// (REACT_APP_* is inlined at build; default port 7003 must match the backend).
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:7003/api';

export interface User {
  id: number;
  azure_ad_id: string;
  email: string;
  name: string;
  created_at: string;
}

export interface AuthResponse {
  message: string;
  redirect_url: string;
  state: string;
}

export interface CallbackResponse {
  message: string;
  user: User;
  redirect_url?: string;
}

export interface MeResponse {
  user: User;
  session: {
    device_id: string;
    created_at: string;
    last_activity: string;
  };
}

class AuthService {
  private accessToken: string | null = null;
  private user: User | null = null;

  /**
   * Initialize login flow - redirects to Azure AD
   * @param redirectUrl Optional URL to redirect to after successful login
   */
  async login(redirectUrl?: string): Promise<void> {
    try {
      const response = await axios.post<AuthResponse>(
        `${API_BASE_URL}/auth/login`,
        { redirect_url: redirectUrl },
        { withCredentials: true }
      );

      // Store state for validation after callback
      sessionStorage.setItem('oauth_state', response.data.state);
      
      // Redirect to Azure AD
      window.location.href = response.data.redirect_url;
    } catch (error) {
      console.error('Login initiation failed:', error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback from Azure AD
   * @param code Authorization code from Azure AD
   * @param state State parameter for CSRF protection
   * @returns Redirect URL if deep linking was used
   */
  async handleCallback(code: string, state: string): Promise<string | null> {
    try {
      // Validate state matches what we stored
      const storedState = sessionStorage.getItem('oauth_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }

      const response = await axios.get<CallbackResponse>(
        `${API_BASE_URL}/auth/callback`,
        {
          params: { code, state },
          withCredentials: true,
        }
      );

      // Store user info
      this.user = response.data.user;
      
      // Clear stored state
      sessionStorage.removeItem('oauth_state');

      // Return redirect URL for deep linking
      return response.data.redirect_url || null;
    } catch (error) {
      console.error('OAuth callback failed:', error);
      sessionStorage.removeItem('oauth_state');
      throw error;
    }
  }

  /**
   * Get current authenticated user info
   */
  async getCurrentUser(): Promise<User> {
    try {
      const response = await axios.get<MeResponse>(
        `${API_BASE_URL}/auth/me`,
        { withCredentials: true }
      );
      
      this.user = response.data.user;
      return response.data.user;
    } catch (error) {
      this.user = null;
      throw error;
    }
  }

  /**
   * Logout - terminates app session (and optionally Azure AD session)
   * @param fullLogout - If true, also logs out from Azure AD (shows account picker)
   */
  async logout(fullLogout: boolean = false): Promise<void> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/logout`,
        { full_logout: fullLogout },
        { withCredentials: true }
      );

      this.user = null;
      this.accessToken = null;

      // Redirect to Azure AD logout if URL provided
      if (response.data.azure_logout_url) {
        window.location.replace(response.data.azure_logout_url);
      } else {
        // Local logout only - show the login screen. `replace` drops the signed-in page from the
        // history, so the Back button cannot return to it.
        window.location.replace('/');
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear local state even if logout fails
      this.user = null;
      this.accessToken = null;
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      if (this.user) {
        return true;
      }
      
      await this.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get cached user (doesn't make API call)
   */
  getCachedUser(): User | null {
    return this.user;
  }

  /**
   * Refresh access token (if needed in future)
   */
  async refreshToken(): Promise<void> {
    try {
      await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Force re-authentication
      this.user = null;
      throw error;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
